/**
 * @fileoverview SolicitudInventarioRepository - Repository for inventory requests management
 */

import { DataRepository } from './base/DataRepository';
import type {
  SolicitudInventario,
  SolicitudInventarioExpanded,
  CreateSolicitudInventarioData,
  UpdateSolicitudInventarioData,
} from '../types/collections/SolicitudInventario';
import type { ListResult, QueryOptions } from '../types/common/PocketBaseTypes';
import { RepositoryError } from './base/DataRepository';
import { createModuleLogger } from '../utils/logger';

/**
 * Repository for managing inventory requests (solicitud_inventario collection)
 * Extends DataRepository with domain-specific methods
 */
export class SolicitudInventarioRepository extends DataRepository<SolicitudInventario> {
  private logger = createModuleLogger('SolicitudInventarioRepository');

  constructor() {
    super('solicitud_inventario');
  }

  /**
   * Get pending solicitudes (not completed and active)
   */
  async getPending(
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<SolicitudInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: 'completado=false && activo=true',
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta',
        sort: '-created',
      });

      this.logger.debug('Fetched pending solicitudes', { count: result.items.length });
      return result as ListResult<SolicitudInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener solicitudes pendientes: ${error instanceof Error ? error.message : String(error)}`,
        'GET_PENDING_ERROR'
      );
    }
  }

  /**
   * Get solicitudes for a specific order
   */
  async getByOrden(
    ordenId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<SolicitudInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: `orden_produccion="${ordenId}" && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta,usuario',
        sort: '-created',
      });

      this.logger.debug('Fetched solicitudes for order', { ordenId, count: result.items.length });
      return result as ListResult<SolicitudInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener solicitudes de la orden ${ordenId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_ORDEN_ERROR'
      );
    }
  }

  /**
   * Get requests for multiple orders in a single call
   */
  async getByOrdenes(ordenIds: string[]): Promise<ListResult<SolicitudInventarioExpanded>> {
    if (!ordenIds || ordenIds.length === 0) {
      return { page: 1, perPage: 0, totalItems: 0, totalPages: 0, items: [] };
    }

    const filterOr = ordenIds.map(id => `orden_produccion="${id}"`).join(' || ');
    try {
      const result = await this.getAll({
        filter: `(${filterOr}) && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta,usuario',
        sort: '-created'
      });

      return {
        page: 1,
        perPage: result.length,
        totalItems: result.length,
        totalPages: 1,
        items: result as unknown as SolicitudInventarioExpanded[]
      };
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener solicitudes para múltiples órdenes: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_ORDENES_ERROR'
      );
    }
  }

  /**
   * Get solicitudes by producto_inventario
   */
  async getByProductoInventario(
    productoId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<ListResult<SolicitudInventarioExpanded>> {
    try {
      const result = await this.getList(page, pageSize, {
        filter: `producto_inventario="${productoId}" && activo=true`,
        expand: 'orden_produccion,producto_inventario,producto_inventario.tinta',
        sort: '-created',
      });

      this.logger.debug('Fetched solicitudes for producto', {
        productoId,
        count: result.items.length,
      });
      return result as ListResult<SolicitudInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener solicitudes del producto ${productoId}: ${error instanceof Error ? error.message : String(error)}`,
        'GET_BY_PRODUCTO_ERROR'
      );
    }
  }

  /**
   * Create a new solicitud with expanded relations
   */
  async createWithExpand(
    data: CreateSolicitudInventarioData
  ): Promise<SolicitudInventarioExpanded> {
    try {
      const created = await this.create(data);
      return this.getById(created.id, {
        expand: 'orden_produccion,producto_inventario',
      }) as Promise<SolicitudInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al crear solicitud: ${error instanceof Error ? error.message : String(error)}`,
        'CREATE_ERROR'
      );
    }
  }

  /**
   * Mark solicitud as completado
   */
  async markAsCompleted(id: string): Promise<SolicitudInventarioExpanded> {
    try {
      const updated = await this.update(id, { completado: true });
      return this.getById(updated.id, {
        expand: 'orden_produccion,producto_inventario',
      }) as Promise<SolicitudInventarioExpanded>;
    } catch (error) {
      throw new RepositoryError(
        `Error al marcar solicitud como completada: ${error instanceof Error ? error.message : String(error)}`,
        'MARK_COMPLETED_ERROR'
      );
    }
  }

  /**
   * Soft delete by marking as inactive
   */
  async softDelete(id: string): Promise<boolean> {
    try {
      await this.update(id, { activo: false });
      this.logger.debug('Soft deleted solicitud', { id });
      return true;
    } catch (error) {
      throw new RepositoryError(
        `Error al eliminar solicitud: ${error instanceof Error ? error.message : String(error)}`,
        'SOFT_DELETE_ERROR'
      );
    }
  }

  /**
   * Get all solicitudes for export (active and not completed)
   * Returns all records without pagination for Excel export
   */
  async getSolicitudesParaExportacion(): Promise<SolicitudInventarioExpanded[]> {
    try {
      const solicitudes = await this.pb.collection(this.collectionName).getFullList<SolicitudInventarioExpanded>({
        filter: 'activo = true && completado = false',
        expand: 'orden_produccion,orden_produccion.cliente,orden_produccion.producto,producto_inventario,producto_inventario.tinta',
        sort: '-created',
      });

      this.logger.debug('Fetched solicitudes for export', { count: solicitudes.length });
      return solicitudes;
    } catch (error) {
      throw new RepositoryError(
        `Error al obtener solicitudes para exportación: ${error instanceof Error ? error.message : String(error)}`,
        'GET_SOLICITUDES_EXPORT_ERROR'
      );
    }
  }
}
